import PersonIcon from "@mui/icons-material/Person";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import type { Nurse } from "../../../../utils/models";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { type DropdownItem, SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../UIToolkit/Inputs/SystemValidationTextField";
import { CharCounter, maxCharsDefault } from "../CharCounter";

export type MessageBoxProps = {
	messageValue: string;
	handleMessageChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	selectedNurseList: Nurse[];
	nursesOptions: DropdownItem<Nurse>[];
	onDropdownSelectionChange: (selectedValues: Nurse[]) => void;
	isMobile?: boolean;
	lockRecipientSelection?: boolean;
};

export const MessageBox: FC<MessageBoxProps> = ({
	messageValue,
	handleMessageChange,
	selectedNurseList,
	nursesOptions,
	onDropdownSelectionChange,
	isMobile,
	lockRecipientSelection,
}) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const recipientNames = selectedNurseList.map(formatPersonName).join(", ");

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<Box style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>Will be sent to</Box>
					<Box
						style={{ fontWeight: 600 }}
						sx={{
							display: "flex",
							alignItems: "center",
						}}
					>
						{<PersonIcon style={{ fontSize: "18px" }} />} {selectedNurseList.length}
						<InHouseTooltip title={"Amount of nurses that can receive this message depends on which nurses have an associated user"}>
							<span style={{ color: "red" }}>*</span>
						</InHouseTooltip>
					</Box>
				</Box>

				<SystemDropdown
					label={"Select Staff Members"}
					showFilter
					items={nursesOptions}
					valueToString={formatPersonName}
					selectedItems={selectedNurseList}
					multiple
					disabled={lockRecipientSelection}
					// Locked: name the recipients. The default summary shows "All (N)", which with one locked
					// option reads "All (1)" in place of the name the message is addressed to.
					renderValue={lockRecipientSelection ? () => recipientNames : undefined}
					formControlSx={{
						width: isMobile ? "140px" : "250px",
					}}
					onChangeMultiple={onDropdownSelectionChange}
				/>
			</Box>
			<Box
				sx={{
					position: "relative",
					width: "100%",
					py: 0.5,
				}}
			>
				<SystemValidationTextField
					variant={isMobile ? "outlined" : "standard"}
					reserveHeightForHelperText={false}
					injectedProps={{
						multiline: true,
						rows: 5,
						slotProps: {
							htmlInput: {
								maxLength: maxCharsDefault,
								style: {
									scrollbarWidth: "thin",
									scrollbarColor: "#888 transparent",
								},
							},
						},
					}}
					formControlSx={{
						width: "100%",
					}}
					value={messageValue}
					onChange={handleMessageChange}
					placeholder={t("type.your.message")}
				/>

				<CharCounter
					text={messageValue}
					sxProps={{
						position: "absolute",
						top: -6,
						right: 0,
					}}
				/>
			</Box>
		</>
	);
};
